'use client';

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { cn } from '@/lib/cn';
import { FilterChip } from '@/components/ui';
import { ListingCard } from '@/components/cards/ListingCard';
import type { Listing } from '@repo/api';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useImageBrowseReporter } from '@/hooks/useImageBrowseReporter';
import { analyticsDataAttrs } from '@/lib/analytics/data-attrs';
import { urgencyParam } from '@/lib/listings/urgency';

interface BedroomCounts {
  studio: number;
  oneBed: number;
  twoBedPlus: number;
}

interface ListingsFilterProps {
  listings: Listing[];
  bedroomCounts: BedroomCounts;
  totalCount: number;
}

type FilterType = 'all' | 'studio' | 'oneBed' | 'twoBedPlus';

export function ListingsFilter({ listings, bedroomCounts, totalCount }: ListingsFilterProps) {
  const searchParams = useSearchParams();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const analytics = useAnalytics();
  const { reportImageBrowse } = useImageBrowseReporter('building_available');

  // Set initial filter from URL params
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && ['studio', 'oneBed', 'twoBedPlus'].includes(filterParam)) {
      setActiveFilter(filterParam as FilterType);
    }
  }, [searchParams]);

  const filteredListings = listings.filter(listing => {
    switch (activeFilter) {
      case 'studio':
        return listing.numBedrooms === 0;
      case 'oneBed':
        return listing.numBedrooms === 1;
      case 'twoBedPlus':
        return listing.numBedrooms >= 2;
      default:
        return true;
    }
  });

  const filters = [
    {
      id: 'all' as FilterType,
      label: 'All',
      count: totalCount,
    },
    {
      id: 'studio' as FilterType,
      label: 'Studio',
      count: bedroomCounts.studio,
    },
    {
      id: 'oneBed' as FilterType,
      label: '1 bed',
      count: bedroomCounts.oneBed,
    },
    {
      id: 'twoBedPlus' as FilterType,
      label: '2+ beds',
      count: bedroomCounts.twoBedPlus,
    },
  ];

  return (
    <div className={cn('flex flex-col gap-6 md:gap-8')}>
      {/* Filter Chips */}
      <div
        className={cn(
          'flex items-start gap-0.5', // 2px gap on mobile
          'overflow-x-auto no-scrollbar -mx-2 px-2',
          'flex-nowrap',
          'md:overflow-visible md:mx-0 md:px-0 md:flex-wrap md:gap-2', // 8px gap on desktop
        )}
      >
        {filters.map(filter => (
          <FilterChip
            key={filter.id}
            active={activeFilter === filter.id}
            onClick={() => {
              switch (filter.id) {
                case 'all':
                  analytics.track('building_listing_filter_all');
                  break;
                case 'studio':
                  analytics.track('building_listing_filter_studio');
                  break;
                case 'oneBed':
                  analytics.track('building_listing_filter_1bed');
                  break;
                case 'twoBedPlus':
                  analytics.track('building_listing_filter_2plusbed');
                  break;
              }
              setActiveFilter(filter.id);
            }}
            label={
              <div className={cn('flex items-center gap-1 text-body-2 text-nowrap')}>
                <span className="text-primary">{filter.label}</span>
                <span className="text-secondary">·</span>
                <span className="text-secondary">{filter.count}</span>
              </div>
            }
            showTrailingIcon={false}
          />
        ))}
      </div>

      {/* Listings Grid */}
      <div
        className={cn(
          'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
          'gap-x-4 gap-y-8 md:gap-y-6',
        )}
      >
        {filteredListings.map(listing => (
          <ListingCard
            key={listing.id}
            listing={listing}
            onQuickView={() => {
              reportImageBrowse(listing.slug);
              analytics.track('building_listingcard_quickview', {
                unitId: listing.slug,
                ...urgencyParam(listing),
              });
            }}
            {...analyticsDataAttrs('building_listingcard', {
              unitId: listing.slug,
              ...urgencyParam(listing),
            })}
          />
        ))}
      </div>

      {/* No results message */}
      {filteredListings.length === 0 && (
        <div className={cn('flex flex-col items-center justify-center py-16 text-center')}>
          <h3 className={cn('text-body-1 font-semibold text-primary mb-2')}>No apartments found</h3>
          <p className={cn('text-body-2 text-secondary')}>
            Try looking for a different number of bedrooms.
          </p>
        </div>
      )}
    </div>
  );
}
